import { useCallback } from 'react';

import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

/** One waterfall bundle's "Hide entities with $0 value" option. */
export interface HideZeroDollarItemsSetting {
  bundleId: string;
  hideZeroDollarItems: boolean;
}

/**
 * The settings that differ from the saved ones. A bundle that was not saved
 * before is compared with the default, which is false.
 */
export function getHideZeroDollarItemsChanges(
  saved: HideZeroDollarItemsSetting[],
  next: HideZeroDollarItemsSetting[]
): HideZeroDollarItemsSetting[] {
  const savedById = new Map(
    saved.map(({ bundleId, hideZeroDollarItems }) => [
      bundleId,
      hideZeroDollarItems,
    ])
  );

  return next.filter(
    ({ bundleId, hideZeroDollarItems }) =>
      hideZeroDollarItems !== (savedById.get(bundleId) ?? false)
  );
}

/**
 * Tracks each change to the option. Call it only after the save that carries
 * the changes succeeds.
 */
export function useTrackHideZeroDollarItemsChanges(
  source: 'presentation' | 'template'
) {
  const trackUserEvent = useTrackUserEvent();

  return useCallback(
    (changes: HideZeroDollarItemsSetting[]) => {
      changes.forEach(({ hideZeroDollarItems }) => {
        trackUserEvent(
          'presentation waterfall_summary_hide_zero_dollar_items_changed',
          { hideZeroDollarItems, source }
        );
      });
    },
    [source, trackUserEvent]
  );
}
